import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Ship, Anchor, DollarSign, TrendingUp, AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { BunkerPriceChart } from '../components/BunkerPriceChart';
import { FreightRateChart } from '../components/FreightRateChart';
import { VesselMap } from '../components/VesselMap';
import { ConversationalSidebar } from '../components/ConversationalSidebar';
import { useTheme } from '../components/ThemeProvider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardPage() {
  const { theme } = useTheme();
  const [vessels, setVessels] = useState([]);
  const [ports, setPorts] = useState([]);
  const [bunkerPrices, setBunkerPrices] = useState([]);
  const [freightRates, setFreightRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const fetchData = async () => {
    try {
      console.log('[Dashboard] Fetching data...');
      const [vesselsRes, portsRes, bunkerRes, freightRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/vessels`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/ports`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/bunker-prices`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/freight-rates`, { withCredentials: true }),
      ]);

      console.log('[Dashboard] Data fetched successfully');
      setVessels(vesselsRes.data);
      setPorts(portsRes.data);
      setBunkerPrices(bunkerRes.data);
      setFreightRates(freightRes.data);
    } catch (error) {
      console.error('[Dashboard] Error fetching data:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || 'Failed to load data';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const seedData = async () => {
    setSeeding(true);
    try {
      console.log('[Dashboard] Seeding data...');
      const response = await axios.post(`${BACKEND_URL}/api/seed-data`, {}, { withCredentials: true });
      console.log('[Dashboard] Seed response:', response.data);
      toast.success('Mock data generated successfully!');
      await fetchData();
    } catch (error) {
      console.error('[Dashboard] Error seeding data:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || 'Failed to seed data. Please try logging in again.';
      toast.error(errorMsg);
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="dashboard-loading">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-[hsl(var(--muted-foreground))]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const avgCongestion = ports.length > 0
    ? (ports.reduce((sum, p) => sum + p.congestion_index, 0) / ports.length).toFixed(2)
    : 0;

  const activeVessels = vessels.filter(v => v.status === 'underway').length;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'var(--font-display)' }}>
            Maritime Pulse Dashboard
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-1">
            Real-time maritime intelligence at your fingertips
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={fetchData}
            variant="outline"
            size="sm"
            data-testid="refresh-data-button"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {vessels.length === 0 && (
            <Button
              onClick={seedData}
              disabled={seeding}
              size="sm"
              data-testid="seed-data-button"
              className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
            >
              {seeding ? 'Generating...' : 'Generate Mock Data'}
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card data-testid="kpi-vessels">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Vessels</CardTitle>
            <Ship className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ fontFamily: 'var(--font-display)' }}>
              {activeVessels}
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              of {vessels.length} total vessels
            </p>
          </CardContent>
        </Card>

        <Card data-testid="kpi-ports">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tracked Ports</CardTitle>
            <Anchor className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ fontFamily: 'var(--font-display)' }}>
              {ports.length}
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              Avg congestion: {avgCongestion}
            </p>
          </CardContent>
        </Card>

        <Card data-testid="kpi-bunker">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Bunker Prices</CardTitle>
            <DollarSign className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ fontFamily: 'var(--font-display)' }}>
              {bunkerPrices.length}
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              Latest updates available
            </p>
          </CardContent>
        </Card>

        <Card data-testid="kpi-freight">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Freight Lanes</CardTitle>
            <TrendingUp className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ fontFamily: 'var(--font-display)' }}>
              {freightRates.length}
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              Active rate quotes
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      {(bunkerPrices.length > 0 || freightRates.length > 0) && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <BunkerPriceChart data={bunkerPrices} theme={theme} />
          <FreightRateChart data={freightRates} theme={theme} />
        </div>
      )}

      {/* Vessel Map */}
      {vessels.length > 0 && (
        <VesselMap vessels={vessels} theme={theme} />
      )}

      {/* Data Tables */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Vessels Table */}
        <Card data-testid="vessels-table-card">
          <CardHeader>
            <CardTitle>Recent Vessels</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--secondary))]">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Vessel</th>
                    <th className="px-4 py-2 text-left font-medium">Class</th>
                    <th className="px-4 py-2 text-left font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {vessels.slice(0, 5).map((vessel) => (
                    <tr key={vessel.id} className="border-t hover:bg-[hsl(var(--secondary))]/50" data-testid={`vessel-row-${vessel.imo}`}>
                      <td className="px-4 py-2">
                        <div>
                          <div className="font-medium">{vessel.name}</div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))]" style={{ fontFamily: 'var(--font-mono)' }}>
                            {vessel.imo}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2">{vessel.vessel_class}</td>
                      <td className="px-4 py-2">
                        <Badge variant="outline">{vessel.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Ports Table */}
        <Card data-testid="ports-table-card">
          <CardHeader>
            <CardTitle>Port Congestion</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--secondary))]">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Port</th>
                    <th className="px-4 py-2 text-left font-medium">Congestion</th>
                    <th className="px-4 py-2 text-left font-medium">Vessels</th>
                  </tr>
                </thead>
                <tbody>
                  {ports.map((port) => (
                    <tr key={port.id} className="border-t hover:bg-[hsl(var(--secondary))]/50" data-testid={`port-row-${port.locode}`}>
                      <td className="px-4 py-2">
                        <div>
                          <div className="font-medium">{port.name}</div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))]">
                            {port.country}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <Badge 
                          variant="outline"
                          className={port.congestion_index > 0.7 ? 'border-[hsl(var(--warning))] text-[hsl(var(--warning))]' : ''}
                        >
                          {(port.congestion_index * 100).toFixed(0)}%
                        </Badge>
                      </td>
                      <td className="px-4 py-2">{port.active_vessels}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Empty State */}
      {vessels.length === 0 && (
        <Card className="mt-8" data-testid="empty-state-card">
          <CardContent className="py-16">
            <div className="text-center space-y-4">
              <AlertCircle className="h-12 w-12 text-[hsl(var(--muted-foreground))] mx-auto" />
              <h3 className="text-lg font-semibold">No Data Available</h3>
              <p className="text-[hsl(var(--muted-foreground))] max-w-md mx-auto">
                Click "Generate Mock Data" above to populate the dashboard with sample maritime data.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Conversational Sidebar */}
      <ConversationalSidebar 
        module="dashboard"
        context={{
          vessels: vessels,
          ports: ports,
          bunkerPrices: bunkerPrices,
          freightRates: freightRates
        }}
        onAction={(type, params) => {
          console.log('[Dashboard] AI Action:', type, params);
          // Actions can be implemented: navigate, create alerts, etc.
        }}
      />
    </div>
  );
}